import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography
} from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db, storage } from "../firebase-key";

const SingleUser = () => {
  const { uid } = useParams();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // -----------------------------------------
  // FETCH USER DATA
  // -----------------------------------------
  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, "users", uid));
      if (snap.exists()) {
        setUserData(snap.data());
      }
    } catch (err) {
      console.error("User fetch failed:", err);
    }finally{
      setLoading(false);
    }
  },[uid]);

  // -----------------------------------------
  // FETCH DOCUMENTS
  // -----------------------------------------
  const fetchDocs = useCallback(async () => {
    const folderRef = ref(storage, `documents/${uid}`);

    try {
      const res = await listAll(folderRef);
      const fileArr = [];

      for (let item of res.items) {
        try {
          const url = await getDownloadURL(item);
          fileArr.push({ name: item.name, url });
        } catch (err) {
          console.warn("Failed URL:", err);
        }
      }

      setDocs(fileArr);
    } catch (err) {
      console.log("Folder empty / error:", err);
      setDocs([]);
    }finally{
      setLoading(false);
    }
  },[uid]);

  useEffect(() => {
    (async () => {
      await fetchUser();
      await fetchDocs();
    })();
  }, [fetchDocs, fetchUser, uid]);

  if (loading) return <p className="pt-28 text-center">Loading...</p>;
  if (!userData) return <p>User not found</p>;

  const format = (v) =>
    v === "" || v === null || v === undefined ? "-" : String(v);

  return (
    <Box sx={{ p: 4, pt: 20 }}>
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 3 }}>
        ← Back
      </Button>

      <Typography variant="h4" fontWeight={700} mb={2}>
        User Details
      </Typography>

      {/* ------------------ USER INFO ------------------ */}
      <Box
        sx={{
          background: "#fff",
          p: 3,
          borderRadius: 2,
          boxShadow: "0px 2px 5px rgba(0,0,0,0.1)"
        }}
      >
        <Typography variant="h6" mb={1}>
          Personal Information
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {Object.entries(userData)
            .filter(([k]) => !["password","created_at" ,"uid","docs"].includes(k))
            .map(([key, value]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Typography variant="subtitle2" color="text.secondary">
                  {key.replace(/_/g, " ").toUpperCase()}
                </Typography>
                <Typography variant="body1">{format(value)}</Typography>
              </Grid>
            ))}
        </Grid>
      </Box>

      {/* ------------------ DOCUMENTS ------------------ */}
      <Box
        sx={{
          background: "#fff",
          p: 3,
          borderRadius: 2,
          mt: 4,
          boxShadow: "0px 2px 5px rgba(0,0,0,0.1)"
        }}
      >
        <Typography variant="h6" mb={1}>
          Uploaded Documents
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {docs.length === 0 ? (
          <Typography>No documents uploaded.</Typography>
        ) : (
          <List>
            {docs.map((d, i) => (
              <ListItem key={i}>
                <ListItemText
                  primary={d.name}
                  secondary="Click to download"
                  onClick={() => window.open(d.url, "_blank")}
                  sx={{ cursor: "pointer" }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default SingleUser;
